import {
  TreatmentPlan,
  Modalities,
} from '../../../../../packages/ui/templates/sign-off/components/modalities';
import { useEffect, useState } from 'react';
import { sx } from '../SessionSignOffV4.styles';
import {
  consumeAllTreatmentRecommendations,
  getAllModalities,
  getPatientModalities,
  getPatientTreatmentPlan,
  getPatientTreatmentPlanRecommendations,
  updatePatientModalities,
  updatePatientTreatmentPlan,
} from '../../../../../services/profile/profile.service';
import {
  createInteractiveProblem,
  getInteractiveGoals,
  getInteractiveObjectives,
} from '../../../../../services/report-service/reportService.service';
import { ModalitiesDrawer } from './ModalitiesDrawer';
import { ProblemDrawer } from './ProblemDrawer';
import { GoalDrawer } from './GoalDrawer';
import { showSnackbar } from '../../../../../redux/modules/snackbar';
import { useDispatch } from 'react-redux';
import { EditTreatmentPlanDrawer } from './EditTreatmentPlanDrawer';
import { Box } from '@mui/material';
import {
  InteractiveProblemStep,
  InteractiveGoalsStep,
  InteractiveObjectivesStep,
} from '../../../../../packages/ui/templates/sign-off/components/interactive-treatment-plan';

export const TreatmentPlanSection = ({
  memberId,
  providerId,
  appointmentId,
  practitionerDesignation,
  // waitingForAI,
  onRecommendationsChange,
  onInteractiveRationaleChange,
  onInteractiveStateChange,
  onInteractiveLoadingChange,
}) => {
  const dispatch = useDispatch();
  const [allModalities, setAllModalities] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [treatmentPlan, setTreatmentPlan] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [openEditDrawer, setOpenEditDrawer] = useState(false);

  // Interactive mode states
  const [isInteractiveMode, setIsInteractiveMode] = useState(false);
  const [interactiveStep, setInteractiveStep] = useState('problem'); // 'problem', 'goals', 'objectives'
  const [interactiveProblem, setInteractiveProblem] = useState(null);
  const [interactiveGoals, setInteractiveGoals] = useState([]);
  const [interactiveObjectives, setInteractiveObjectives] = useState([]);
  const [isLoadingInteractive, setIsLoadingInteractive] = useState(false);

  const fetchAllModalities = async () => {
    try {
      const response = await getAllModalities();
      if (response?.data) {
        setAllModalities(response.data.modalities || []);
      }
    } catch (error) {
      console.error('Error fetching modalities:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to load modalities. Please try again.',
        }),
      );
    }
  };

  const fetchPatientModalities = async () => {
    try {
      const response = await getPatientModalities(providerId);
      if (response?.data) {
        setModalities(response.data.modalities);
      }
    } catch (error) {
      console.error('Error fetching patient modalities:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to load patient modalities. Please try again.',
        }),
      );
    }
  };

  const fetchTreatmentPlan = async () => {
    try {
      const response = await getPatientTreatmentPlan(memberId, { active: false });
      if (response?.data) {
        const plan = response.data;
        setTreatmentPlan(plan);

        // Check if this should be interactive mode
        if (plan.isFirstTreatmentPlan) {
          setIsInteractiveMode(true);
          // Determine step based on plan structure
          determineInteractiveStep(plan);
        }
      }
    } catch (error) {
      console.error('Error fetching patient treatment Plan:', error);

      // Check if error is NOT_FOUND - trigger interactive mode for new plan
      if (error?.data?.errors?.[0]?.errorCode === 'NOT_FOUND') {
        setIsInteractiveMode(true);
        setInteractiveStep('problem');
        setInteractiveProblem({ detail: '', rationale: null });
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Failed to load treatment plan. Please try again.',
          }),
        );
      }
    }
  };

  const fetchTreatmentPlanRecommendations = async () => {
    try {
      const response = await getPatientTreatmentPlanRecommendations(appointmentId);
      if (response?.data) {
        // Filter out recommendations that have already been applied
        const pendingRecommendations =
          response.data?.recommendations?.filter(rec => rec.status !== 'APPLIED') || [];
        setRecommendations(pendingRecommendations);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to load recommendations. Please try again.',
        }),
      );
    }
  };

  useEffect(() => {
    void fetchAllModalities();
  }, []);

  useEffect(() => {
    if (providerId) {
      void fetchPatientModalities();
      void fetchTreatmentPlan();
    }
  }, [providerId]);

  useEffect(() => {
    if (appointmentId) {
      void fetchTreatmentPlanRecommendations();
    }
  }, [appointmentId]);

  // Notify parent when recommendations or treatmentPlan change
  useEffect(() => {
    if (onRecommendationsChange) {
      onRecommendationsChange(
        recommendations || [],
        handleApplyRecommendation,
        handleApplyAllRecommendations,
      );
    }
  }, [recommendations, treatmentPlan]);

  // Notify parent when interactive state changes
  useEffect(() => {
    if (onInteractiveStateChange) {
      let saveHandler = null;
      let canProceed = true;

      if (isInteractiveMode) {
        switch (interactiveStep) {
          case 'problem':
            saveHandler = handleSaveProblem;
            canProceed = !!interactiveProblem?.detail?.trim();
            break;
          case 'goals':
            saveHandler = handleSaveGoals;
            canProceed = interactiveGoals.some(g => g.preSelected);
            break;
          case 'objectives':
            saveHandler = handleSaveObjectives;
            canProceed = interactiveGoals.some(g => g.objectives?.some(obj => obj.preSelected !== false));
            break;
          default:
            break;
        }
      }

      onInteractiveStateChange(isInteractiveMode, interactiveStep, saveHandler, canProceed);
    }
  }, [isInteractiveMode, interactiveStep, interactiveProblem, interactiveGoals]);

  // Notify parent when interactive loading state changes
  useEffect(() => {
    if (onInteractiveLoadingChange) {
      onInteractiveLoadingChange(isLoadingInteractive);
    }
  }, [isLoadingInteractive]);

  const handleModalitiesSubmit = async updatedModalities => {
    try {
      setModalities(updatedModalities);
      await updatePatientModalities(providerId, {
        modalities: updatedModalities,
      });
      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: 'Modalities updated successfully.',
        }),
      );
    } catch (error) {
      console.error('Error updating modalities:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to update modalities. Please try again.',
        }),
      );
    }
  };

  const saveTreatmentPlan = async (updatedPlan, recommendationIds = null) => {
    if (!updatedPlan) return;
    // Process the plan before saving
    const planToSave = JSON.parse(JSON.stringify(updatedPlan));

    // Convert _id to id for API
    if (planToSave._id) {
      planToSave.id = planToSave._id;
      delete planToSave._id;
    }

    // Set the recommendationIds at top level - this will track which recommendations were applied
    planToSave.recommendationIds = recommendationIds;

    // Strip ID from new problems, goals and objectives only if `new` property is set to true
    planToSave.problems = updatedPlan.problems.map(problem => {
      // Clean up the problem object
      const cleanProblem = { ...problem };

      // Convert _id to id
      if (cleanProblem._id) {
        cleanProblem.id = cleanProblem._id;
        delete cleanProblem._id;
      }

      if (problem.new) {
        cleanProblem.id = null;
      }

      // Process goals
      cleanProblem.goals = problem.goals.map(goal => {
        const cleanGoal = { ...goal };

        // Convert _id to id
        if (cleanGoal._id) {
          cleanGoal.id = cleanGoal._id;
          delete cleanGoal._id;
        }

        if (goal.new) {
          cleanGoal.id = null;
        }

        // Process objectives
        cleanGoal.objectives = goal.objectives.map(objective => {
          const cleanObjective = { ...objective };

          // Convert _id to id
          if (cleanObjective._id) {
            cleanObjective.id = cleanObjective._id;
            delete cleanObjective._id;
          }

          if (objective.new) {
            cleanObjective.id = null;
          }
          // Remove any temporary properties
          delete cleanObjective.new;
          return cleanObjective;
        });
        // Remove any temporary properties
        delete cleanGoal.new;
        delete cleanGoal.problemId;
        delete cleanGoal.recommendationIds; // Remove local tracking since we use the top-level one
        return cleanGoal;
      });
      // Remove any temporary properties
      delete cleanProblem.new;
      delete cleanProblem.recommendationIds; // Remove local tracking since we use the top-level one
      return cleanProblem;
    });

    try {
      const response = await updatePatientTreatmentPlan(memberId, planToSave);
      if (response.data) {
        setTreatmentPlan(response.data);
        // If we applied recommendations, refetch recommendations to update the list
        if (recommendationIds && recommendationIds.length > 0 && appointmentId) {
          await fetchTreatmentPlanRecommendations();
        }
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Treatment plan updated successfully.',
          }),
        );
        return response;
      }
    } catch (error) {
      console.error('Error saving treatment plan:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save treatment plan. Please try again.',
        }),
      );
      throw error;
    }
  };

  const handleProblemSubmit = async updatedProblem => {
    try {
      // Extract recommendationIds before processing
      const recommendationIds = updatedProblem.recommendationIds || [];

      const updatedPlan = {
        ...treatmentPlan,
        problems: treatmentPlan.problems.some(p => p.id === updatedProblem.id)
          ? treatmentPlan.problems.map(p => (p.id === updatedProblem.id ? updatedProblem : p))
          : [...treatmentPlan.problems, updatedProblem],
      };
      setTreatmentPlan(updatedPlan);
      setSelectedProblem(null);

      // Pass recommendationIds to saveTreatmentPlan
      await saveTreatmentPlan(updatedPlan, recommendationIds);
    } catch (error) {
      console.error('Error submitting problem:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save problem. Please try again.',
        }),
      );
    }
  };

  const handleProblemDelete = async problemId => {
    try {
      const updatedPlan = {
        ...treatmentPlan,
        problems: treatmentPlan.problems.filter(p => p.id !== problemId),
      };
      setTreatmentPlan(updatedPlan);
      setSelectedProblem(null);
      await saveTreatmentPlan(updatedPlan);
    } catch (error) {
      console.error('Error deleting problem:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to delete problem. Please try again.',
        }),
      );
    }
  };

  const handleGoalSubmit = async updatedGoal => {
    try {
      // Extract recommendationIds before we process the goal
      const recommendationIds = updatedGoal.recommendationIds || [];

      // Make a deep copy to avoid reference issues
      const processedGoal = JSON.parse(JSON.stringify(updatedGoal));

      // Store the problem ID if it was passed in the original goal
      const problemId = processedGoal.problemId;
      delete processedGoal.problemId; // Remove it as it's not part of the data model

      // Create updated plan
      let updatedPlan;

      // If we know which problem this goal belongs to
      if (problemId) {
        updatedPlan = {
          ...treatmentPlan,
          problems: treatmentPlan.problems.map(problem => {
            if (problem.id === problemId) {
              // Return updated problem with goal replaced or added
              const existingGoalIndex = problem.goals.findIndex(g => g.id === processedGoal.id);

              if (existingGoalIndex >= 0) {
                // Replace existing goal
                const updatedGoals = [...problem.goals];
                updatedGoals[existingGoalIndex] = processedGoal;

                return {
                  ...problem,
                  goals: updatedGoals,
                };
              } else {
                // Add as new goal
                return {
                  ...problem,
                  goals: [...problem.goals, processedGoal],
                };
              }
            }
            return problem;
          }),
        };
      } else {
        // Try to find the goal in any problem
        let foundInProblem = false;

        updatedPlan = {
          ...treatmentPlan,
          problems: treatmentPlan.problems.map(problem => {
            // Check if this goal exists in the current problem
            const goalIndex = problem.goals.findIndex(g => g.id === processedGoal.id);

            if (goalIndex >= 0) {
              foundInProblem = true;

              // Replace the goal at the same index
              const updatedGoals = [...problem.goals];
              updatedGoals[goalIndex] = processedGoal;

              return {
                ...problem,
                goals: updatedGoals,
              };
            }

            // Not in this problem, return unchanged
            return problem;
          }),
        };

        // If goal wasn't found in any problem, it must be new
        // Add it to the first problem as a fallback
        if (!foundInProblem && processedGoal.new && updatedPlan.problems.length > 0) {
          updatedPlan.problems[0].goals.push(processedGoal);
        }
      }

      // Update state and save
      setTreatmentPlan(updatedPlan);
      setSelectedGoal(null);

      // Pass recommendationIds to saveTreatmentPlan
      await saveTreatmentPlan(updatedPlan, recommendationIds);
    } catch (error) {
      console.error('Error saving goal:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save goal. Please try again.',
        }),
      );
    }
  };

  const handleGoalDelete = async goalId => {
    try {
      const updatedPlan = {
        ...treatmentPlan,
        problems: treatmentPlan.problems.map(p => ({
          ...p,
          goals: p.goals.filter(g => g.id !== goalId),
        })),
      };
      setTreatmentPlan(updatedPlan);
      setSelectedGoal(null);
      await saveTreatmentPlan(updatedPlan);
    } catch (error) {
      console.error('Error deleting goal:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to delete goal. Please try again.',
        }),
      );
    }
  };

  const handleToggleObjectiveCompletion = async (problemId, goalId, objectiveId, isCompleted) => {
    try {
      const updatedPlan = {
        ...treatmentPlan,
        problems: treatmentPlan.problems.map(problem =>
          problem.id === problemId
            ? {
                ...problem,
                goals: problem.goals.map(goal =>
                  goal.id === goalId
                    ? {
                        ...goal,
                        objectives: goal.objectives.map(obj =>
                          obj.id === objectiveId ? { ...obj, isCompleted } : obj,
                        ),
                      }
                    : goal,
                ),
              }
            : problem,
        ),
      };

      setTreatmentPlan(updatedPlan);
      await saveTreatmentPlan(updatedPlan);

      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: `Objective marked as ${isCompleted ? 'completed' : 'incomplete'}.`,
        }),
      );
    } catch (error) {
      console.error('Error toggling objective completion:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to update objective. Please try again.',
        }),
      );
    }
  };

  const updatePlan = async plan => {
    try {
      setTreatmentPlan(plan);
      setOpenEditDrawer(false);
      await saveTreatmentPlan(plan);
    } catch (error) {
      console.error('Error updating plan:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to update treatment plan. Please try again.',
        }),
      );
    }
  };

  const handleAddGoal = problem => {
    setSelectedGoal({
      id: null,
      name: '',
      details: '',
      objectives: [],
      new: true,
      problemId: problem.id,
    });
  };

  const handleAddProblem = () => {
    setSelectedProblem({
      id: null,
      detail: '',
      goals: [],
      new: true,
    });
  };

  const handleApplyRecommendation = recs => {
    if (!recs || recs.length === 0) return;
    if (!treatmentPlan) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Treatment plan not loaded yet. Please wait.',
        }),
      );
      return;
    }

    // Collect all recommendation IDs
    const recommendationIds = recs.map(rec => rec.id);

    // Handle the recommendation based on its type (problem, goal or objective)
    const primaryRec = recs[0];

    if (primaryRec.entityType === 'PROBLEM') {
      if (primaryRec.recommendationType === 'ADD_PROBLEM') {
        // Transform recommendation data into treatment plan structure
        const problemData = {
          detail: primaryRec.recommendedValue,
          status: 'ACTIVE',
          goals: [],
          // Keep original data for reference/save
          recommendationIds: recommendationIds,
        };

        // Find linked goals for this problem
        const linkedGoals = recs.filter(
          rec => rec.recommendationType === 'ADD_GOAL' && rec.entityReference === primaryRec.generatedId,
        );

        // Process each linked goal
        linkedGoals.forEach(goalRec => {
          const goalData = {
            name: goalRec.recommendedValue,
            details: goalRec.rationale || '',
            objectives: [],
          };

          // Find linked objectives for this goal
          const linkedObjectives = recs.filter(
            rec => rec.recommendationType === 'ADD_OBJECTIVE' && rec.entityReference === goalRec.generatedId,
          );

          // Process each linked objective
          linkedObjectives.forEach(objRec => {
            goalData.objectives.push({
              details: objRec.recommendedValue,
            });
          });

          // Add goal to problem data
          problemData.goals.push(goalData);
        });

        setSelectedProblem(problemData);
      } else {
        // For Edit a Problem use original logic
        const existingProblem = treatmentPlan.problems.find(p => p.id === primaryRec.entityReference);
        if (!existingProblem) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: 'Could not find the problem to update. It may have been deleted.',
            }),
          );
          return;
        }
        const problemData = {
          id: primaryRec.entityReference || null,
          detail: primaryRec.recommendedValue,
          goals: existingProblem.goals || [],
          recommendationIds: recommendationIds,
        };
        setSelectedProblem(problemData);
      }
    } else if (primaryRec.entityType === 'GOAL') {
      // Check if this is an ADD_GOAL to an existing problem (not a new problem)
      if (primaryRec.recommendationType === 'ADD_GOAL') {
        // Check if parent problem exists
        const parentProblem = treatmentPlan.problems.find(p => p.id === primaryRec.entityReference);
        if (!parentProblem) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: 'Could not find the problem to add this goal to. It may have been deleted.',
            }),
          );
          return;
        }
        // Find linked objectives for this goal
        const linkedObjectives = recs.filter(
          rec => rec.recommendationType === 'ADD_OBJECTIVE' && rec.entityReference === primaryRec.generatedId,
        );

        // Open goal drawer with prefilled data
        const goalData = {
          id: null,
          name: primaryRec.recommendedValue,
          details: primaryRec.rationale || '',
          objectives: linkedObjectives.map(objRec => ({
            details: objRec.recommendedValue,
            new: true,
          })),
          new: true,
          problemId: primaryRec.entityReference, // This is the problem id
          recommendationIds: recommendationIds,
        };

        setSelectedGoal(goalData);
        return;
      }

      // Find the existing goal in the treatment plan
      let existingGoal = null;
      let problemId = null;
      let foundGoal = false;

      // Search through problems to find the goal
      for (const problem of treatmentPlan?.problems || []) {
        const found = problem.goals.find(g => g.id === primaryRec.entityReference);
        if (found) {
          existingGoal = found;
          problemId = problem.id;
          foundGoal = true;
          break;
        }
      }
      if (!foundGoal) {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Could not find the goal to update. It may have been deleted.',
          }),
        );
        return;
      }

      // Open goal drawer with prefilled data
      const goalData = {
        id: primaryRec?.entityReference || null,
        name: primaryRec.recommendedValue,
        details: existingGoal?.details || primaryRec?.rationale || '',
        objectives: existingGoal?.objectives || [],
        new: primaryRec.recommendationType.startsWith('ADD'),
        problemId: problemId,
        // Store recommendation IDs for the API
        recommendationIds: recommendationIds,
      };

      setSelectedGoal(goalData);
    } else if (primaryRec.entityType === 'OBJECTIVE') {
      // Find the parent goal for this objective
      let parentGoal = null;
      let targetObjectiveIndex = -1;
      let foundExactMatch = false;

      // 1. Search for direct goal match if it's an ADD_OBJECTIVE
      if (primaryRec.recommendationType === 'ADD_OBJECTIVE') {
        for (const problem of treatmentPlan?.problems || []) {
          for (const goal of problem.goals || []) {
            if (goal.id === primaryRec.entityReference) {
              parentGoal = JSON.parse(JSON.stringify(goal));
              parentGoal.problemId = problem.id;
              parentGoal.recommendationIds = recommendationIds;
              foundExactMatch = true;
              break;
            }
          }
          if (foundExactMatch) break;
        }
        if (!foundExactMatch) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: 'Could not find the goal to add this objective to. It may have been deleted.',
            }),
          );
          return;
        }
      }

      // 2. Search for objective match if it's UPDATE_OBJECTIVE
      if (primaryRec.recommendationType === 'UPDATE_OBJECTIVE' && !foundExactMatch) {
        for (const problem of treatmentPlan?.problems || []) {
          for (const goal of problem.goals || []) {
            if (goal.objectives) {
              const objIndex = goal.objectives.findIndex(obj => obj.id === primaryRec.entityReference);
              if (objIndex >= 0) {
                parentGoal = JSON.parse(JSON.stringify(goal));
                parentGoal.problemId = problem.id;
                parentGoal.recommendationIds = recommendationIds;
                targetObjectiveIndex = objIndex;
                foundExactMatch = true;
                break;
              }
            }
          }
          if (foundExactMatch) break;
        }
        if (!foundExactMatch) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: 'Could not find the objective to update. It may have been deleted.',
            }),
          );
          return;
        }
      }

      // Now we have a parent goal or null if nothing could be found
      if (parentGoal) {
        // Create a copy of the goal with the objective added/updated
        const goalCopy = JSON.parse(JSON.stringify(parentGoal));

        // Ensure objectives array exists
        if (!goalCopy.objectives) {
          goalCopy.objectives = [];
        }

        if (primaryRec.recommendationType === 'ADD_OBJECTIVE' || targetObjectiveIndex < 0) {
          // Add as new objective
          const newObjective = {
            id: null,
            details: primaryRec.recommendedValue,
            new: true,
          };

          goalCopy.objectives.push(newObjective);
        } else {
          // Update existing objective
          goalCopy.objectives[targetObjectiveIndex].details = primaryRec.recommendedValue;
        }
        // Open the goal drawer with the updated goal
        setSelectedGoal(goalCopy);
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Unable to find any goal for this objective.',
          }),
        );
      }
    }
  };

  const handleApplyAllRecommendations = async () => {
    try {
      const response = await consumeAllTreatmentRecommendations(appointmentId);
      if (response?.data) {
        // Filter out recommendations that have already been applied
        const pendingRecommendations =
          response.data?.recommendations?.filter(rec => rec.status !== 'APPLIED') || [];
        setRecommendations(pendingRecommendations);
        void fetchTreatmentPlan();
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'All recommendations applied successfully.',
          }),
        );
      }
    } catch (error) {
      console.error('Error applying all recommendations:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to apply recommendations. Please try again.',
        }),
      );
    }
  };

  // Interactive mode helpers
  const determineInteractiveStep = plan => {
    if (!plan.problems || plan.problems.length === 0) {
      setInteractiveStep('problem');
      setInteractiveProblem({ detail: '', rationale: null });
      return;
    }

    const problem = plan.problems[0];
    setInteractiveProblem(problem);

    // If problem exists but no goals, stay on problem step
    // User needs to click Next to trigger goals API
    if (!problem.goals || problem.goals.length === 0) {
      setInteractiveStep('problem');
      if (onInteractiveRationaleChange && problem.rationale) {
        onInteractiveRationaleChange({
          title: 'Potential problem reasoning',
          items: [
            {
              description: problem.rationale,
            },
          ],
        });
      }
      return;
    }

    // If we have goals but no objectives, show goals step
    const hasObjectives = problem.goals.some(goal => goal.objectives && goal.objectives.length > 0);

    if (!hasObjectives) {
      setInteractiveStep('goals');
      setInteractiveGoals(problem.goals);
      if (onInteractiveRationaleChange && problem.goals.some(g => g.rationale)) {
        onInteractiveRationaleChange({
          title: 'Potential goal reasoning',
          items: problem.goals
            .filter(g => g.rationale)
            .map(goal => {
              return {
                title: goal.name,
                description: goal.rationale,
              };
            }),
        });
      }
      return;
    }

    // If we have problem, goals with objectives, show objectives step
    setInteractiveStep('objectives');
    setInteractiveGoals(problem.goals);
    if (
      onInteractiveRationaleChange &&
      problem.goals.some(g => g.objectives?.length > 0 && g.objectives.some(obj => obj.rationale))
    ) {
      onInteractiveRationaleChange({
        title: 'Potential objective reasoning',
        items: problem.goals
          .find(g => g.objectives?.length > 0 && g.objectives.some(obj => obj.rationale))
          ?.objectives?.filter(obj => obj.rationale)
          .map(objective => {
            return {
              title: objective.details,
              description: objective.rationale,
            };
          }),
      });
    }
  };

  // Interactive step handlers
  const handleProblemChange = value => {
    setInteractiveProblem(prev => ({ ...prev, detail: value }));
  };

  const handleSaveProblem = async () => {
    if (!interactiveProblem?.detail?.trim()) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Please enter a problem statement.',
        }),
      );
      return;
    }

    setIsLoadingInteractive(true);
    try {
      // If treatment plan doesn't exist, create it via interactive endpoint
      if (!treatmentPlan) {
        const response = await createInteractiveProblem(appointmentId, {
          presentingProblem: interactiveProblem.detail,
        });

        if (response?.data?.data?.treatmentPlan) {
          const plan = response.data.data.treatmentPlan;
          setTreatmentPlan(plan);
          setInteractiveProblem(plan.problems[0]);

          // Fetch goals for next step
          await fetchInteractiveGoalsData(plan._id || plan.id, plan.problems[0]._id || plan.problems[0].id);
        }
      } else {
        // Update existing plan
        const updatedPlan = {
          ...treatmentPlan,
          problems: [
            {
              ...interactiveProblem,
              status: 'ACTIVE',
              goals: interactiveProblem.goals || [],
            },
          ],
        };

        await saveTreatmentPlan(updatedPlan);

        // Fetch goals for next step
        await fetchInteractiveGoalsData(treatmentPlan.id, interactiveProblem.id);
      }

      setInteractiveStep('goals');
    } catch (error) {
      console.error('Error saving problem:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save problem. Please try again.',
        }),
      );
    } finally {
      setIsLoadingInteractive(false);
    }
  };

  const fetchInteractiveGoalsData = async (treatmentPlanId, problemId) => {
    setIsLoadingInteractive(true);
    try {
      const response = await getInteractiveGoals({
        treatmentPlanId,
        appointmentId,
        problemId,
      });

      if (response?.data?.data?.treatmentPlan) {
        const plan = response.data.data.treatmentPlan;
        setTreatmentPlan(plan);
        const receivedGoals = plan.problems[0].goals;
        setInteractiveGoals(receivedGoals);

        // Notify parent about rationale if exists
        if (onInteractiveRationaleChange && receivedGoals.some(g => g.rationale)) {
          onInteractiveRationaleChange({
            title: 'Potential goal reasoning',
            items: receivedGoals
              .filter(g => g.rationale)
              .map(goal => {
                return {
                  title: goal.name,
                  description: goal.rationale,
                };
              }),
          });
        }
      }
    } catch (error) {
      console.error('Error fetching interactive goals:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to load goal suggestions. Please try again.',
        }),
      );
    } finally {
      setIsLoadingInteractive(false);
    }
  };

  const handleAddInteractiveGoal = goalData => {
    const newGoal = {
      id: `temp-${Date.now()}`,
      name: typeof goalData === 'string' ? goalData : goalData.name,
      details: typeof goalData === 'object' ? goalData.details : '',
      objectives: [],
      preSelected: true,
      isCustom: true,
    };
    // Add new goal at the top of the list
    setInteractiveGoals(prev => [newGoal, ...prev]);
  };

  const handleEditInteractiveGoal = goalData => {
    setInteractiveGoals(prev =>
      prev.map(goal =>
        goal.id === goalData.id ? { ...goal, name: goalData.name, details: goalData.details || '' } : goal,
      ),
    );
  };

  const handleEditGoal = goal => {
    // Open goal drawer for editing
    setSelectedGoal(goal);
  };

  const handleToggleGoalSelection = goalId => {
    setInteractiveGoals(prev =>
      prev.map(goal => (goal.id === goalId ? { ...goal, preSelected: !goal.preSelected } : goal)),
    );
  };

  const handleSaveGoals = async () => {
    const selectedGoals = interactiveGoals.filter(g => g.preSelected);

    if (selectedGoals.length === 0) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Please select at least one goal.',
        }),
      );
      return;
    }

    setIsLoadingInteractive(true);
    try {
      // Update treatment plan with selected goals
      const updatedPlan = {
        ...treatmentPlan,
        problems: [
          {
            ...interactiveProblem,
            goals: selectedGoals.map(g => ({
              ...(g._id && { _id: g._id }),
              ...(g.id && !g.isCustom && { id: g.id }),
              name: g.name,
              details: g.details || '',
              objectives: g.objectives || [],
              ...(g.aiGenerated !== undefined && { aiGenerated: g.aiGenerated }),
              ...(g.preSelected !== undefined && { preSelected: g.preSelected }),
            })),
          },
        ],
      };

      const response = await saveTreatmentPlan(updatedPlan);

      if (response?.data) {
        setTreatmentPlan(response.data);

        // Fetch objectives for next step
        const goalIds = response.data.problems[0].goals.map(g => g._id || g.id);
        await fetchInteractiveObjectivesData(
          response.data._id || response.data.id,
          response.data.problems[0]._id || response.data.problems[0].id,
          goalIds,
        );

        setInteractiveStep('objectives');
      }
    } catch (error) {
      console.error('Error saving goals:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save goals. Please try again.',
        }),
      );
    } finally {
      setIsLoadingInteractive(false);
    }
  };

  const fetchInteractiveObjectivesData = async (treatmentPlanId, problemId, goalIds) => {
    setIsLoadingInteractive(true);
    try {
      const response = await getInteractiveObjectives({
        treatmentPlanId,
        appointmentId,
        problemId,
        goalIds,
      });

      if (response?.data?.data?.treatmentPlan) {
        const plan = response.data.data.treatmentPlan;
        setTreatmentPlan(plan);
        const receivedGoals = plan.problems[0].goals;
        setInteractiveGoals(plan.problems[0].goals);

        // Notify parent about rationale if exists
        if (
          onInteractiveRationaleChange &&
          receivedGoals.some(g => g.objectives?.length > 0 && g.objectives.some(obj => obj.rationale))
        ) {
          onInteractiveRationaleChange({
            title: 'Potential objective reasoning',
            items: receivedGoals
              .find(g => g.objectives?.length > 0 && g.objectives.some(obj => obj.rationale))
              ?.objectives?.filter(obj => obj.rationale)
              .map(objective => {
                return {
                  title: objective.details,
                  description: objective.rationale,
                };
              }),
          });
        }
      }
    } catch (error) {
      console.error('Error fetching interactive objectives:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to load objective suggestions. Please try again.',
        }),
      );
    } finally {
      setIsLoadingInteractive(false);
    }
  };

  const handleToggleObjective = (goalId, objectiveId) => {
    setInteractiveGoals(prev =>
      prev.map(goal =>
        goal.id === goalId
          ? {
              ...goal,
              objectives: goal.objectives.map(obj =>
                obj.id === objectiveId ? { ...obj, preSelected: !obj.preSelected } : obj,
              ),
            }
          : goal,
      ),
    );
  };

  const handleEditObjectiveInline = (goalId, objectiveId, newText) => {
    setInteractiveGoals(prev =>
      prev.map(goal =>
        goal.id === goalId
          ? {
              ...goal,
              objectives: goal.objectives.map(obj =>
                obj.id === objectiveId ? { ...obj, details: newText } : obj,
              ),
            }
          : goal,
      ),
    );
  };

  const handleDeleteGoalFromObjectives = goalId => {
    setInteractiveGoals(prev => prev.filter(g => g.id !== goalId));
  };

  const handleSaveObjectives = async () => {
    setIsLoadingInteractive(true);
    try {
      // Prepare final treatment plan with selected objectives
      const finalGoals = interactiveGoals.map(goal => ({
        ...(goal._id && { _id: goal._id }),
        ...(goal.id && !goal.isCustom && { id: goal.id }),
        name: goal.name,
        details: goal.details || '',
        ...(goal.aiGenerated !== undefined && { aiGenerated: goal.aiGenerated }),
        objectives: goal.objectives
          .filter(obj => obj.preSelected !== false)
          .map(obj => ({
            ...(obj._id && { _id: obj._id }),
            ...(obj.id && !obj.new && { id: obj.id }),
            details: obj.details,
            ...(obj.aiGenerated !== undefined && { aiGenerated: obj.aiGenerated }),
            ...(obj.preSelected !== undefined && { preSelected: obj.preSelected }),
          })),
      }));

      const updatedPlan = {
        ...treatmentPlan,
        problems: [
          {
            ...interactiveProblem,
            goals: finalGoals,
          },
        ],
        isFirstTreatmentPlan: false, // Mark as completed
      };

      await saveTreatmentPlan(updatedPlan);

      // Exit interactive mode
      setIsInteractiveMode(false);

      // Refetch the complete plan
      await fetchTreatmentPlan();

      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: 'Treatment plan created successfully!',
        }),
      );
    } catch (error) {
      console.error('Error saving objectives:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save objectives. Please try again.',
        }),
      );
    } finally {
      setIsLoadingInteractive(false);
    }
  };

  const handleViewReasoning = itemId => {
    // This will be handled by parent component to show rationale in right panel
    if (onInteractiveRationaleChange) {
      let rationale = null;

      if (interactiveStep === 'problem') {
        rationale = {
          title: 'Potential problem reasoning',
          items: [
            {
              description: interactiveProblem?.rationale,
            },
          ],
        };
      } else if (interactiveStep === 'goals') {
        rationale = {
          title: 'Potential goal reasoning',
          items: interactiveGoals
            .filter(g => g.rationale)
            .map(goal => {
              return {
                title: goal.name,
                description: goal.rationale,
              };
            }),
        };
      } else if (interactiveStep === 'objectives') {
        rationale = {
          title: 'Potential objective reasoning',
          items: interactiveGoals
            .find(g => g.id === itemId)
            ?.objectives?.filter(obj => obj.rationale)
            .map(objective => {
              return {
                title: objective.details,
                description: objective.rationale,
              };
            }),
        };
      }

      if (rationale) {
        onInteractiveRationaleChange(rationale);
      }
    }
  };

  return (
    <Box padding="16px">
      <Box display="flex" flexDirection="column" gap="8px" marginBottom={4}>
        <Box sx={sx.subsectionTitle}>Treatment Plan</Box>
        <Box sx={sx.subsectionDescription}>
          Please revise the treatment plan we created to align with your clinical judgment.
        </Box>
      </Box>

      {/* Interactive mode - show step components */}
      {isInteractiveMode ? (
        <Box position="relative">
          {/* Blur overlay when loading */}
          {isLoadingInteractive && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              zIndex={10}
              sx={{
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
              }}
            />
          )}

          {interactiveStep === 'problem' && (
            <Box display="flex" flexDirection="column" gap="24px">
              <InteractiveProblemStep
                problem={interactiveProblem}
                onProblemChange={handleProblemChange}
                onViewReasoning={handleViewReasoning}
              />
              <Modalities modalities={modalities} onAddEditModalities={() => setIsDrawerOpen(true)} />
            </Box>
          )}

          {interactiveStep === 'goals' && (
            <InteractiveGoalsStep
              goals={interactiveGoals}
              onAddGoal={handleAddInteractiveGoal}
              onEditGoal={handleEditInteractiveGoal}
              onToggleGoalSelection={handleToggleGoalSelection}
              onViewReasoning={handleViewReasoning}
            />
          )}

          {interactiveStep === 'objectives' && (
            <InteractiveObjectivesStep
              selectedGoals={interactiveGoals}
              onToggleObjective={handleToggleObjective}
              onEditObjective={handleEditObjectiveInline}
              onAddObjective={(goalId, objectiveText) => {
                // Add new objective to the specified goal
                setInteractiveGoals(prev =>
                  prev.map(goal =>
                    goal.id === goalId
                      ? {
                          ...goal,
                          objectives: [
                            ...(goal.objectives || []),
                            {
                              id: `temp-${Date.now()}`,
                              details: objectiveText,
                              preSelected: true,
                              new: true,
                            },
                          ],
                        }
                      : goal,
                  ),
                );
              }}
              onViewReasoning={handleViewReasoning}
            />
          )}
        </Box>
      ) : (
        /* Regular mode - show standard treatment plan */
        <TreatmentPlan
          onAddEditModalities={() => setIsDrawerOpen(true)}
          onEditProblem={setSelectedProblem}
          onEditGoal={setSelectedGoal}
          onEditTreatmentPlan={() => setOpenEditDrawer(true)}
          onAddGoal={handleAddGoal}
          onAddProblem={handleAddProblem}
          modalities={modalities}
          onDeleteGoal={goalId => handleGoalDelete(goalId)}
          onDeleteProblem={problemId => handleProblemDelete(problemId)}
          onToggleObjectiveCompletion={handleToggleObjectiveCompletion}
          treatmentPlan={treatmentPlan}
          recommendations={recommendations}
        />
      )}
      {isDrawerOpen && (
        <ModalitiesDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          allModalities={allModalities}
          modalities={modalities}
          onSubmit={handleModalitiesSubmit}
        />
      )}
      {selectedProblem && (
        <ProblemDrawer
          isOpen={!!selectedProblem}
          onClose={() => setSelectedProblem(null)}
          problem={selectedProblem}
          onSubmit={handleProblemSubmit}
        />
      )}
      {selectedGoal && (
        <GoalDrawer
          isOpen={!!selectedGoal}
          onClose={() => setSelectedGoal(null)}
          goal={selectedGoal}
          onSubmit={handleGoalSubmit}
        />
      )}
      {openEditDrawer && (
        <EditTreatmentPlanDrawer
          isOpen={!!openEditDrawer}
          onClose={() => setOpenEditDrawer(false)}
          treatmentPlan={treatmentPlan}
          onSubmit={updatePlan}
          practitionerDesignation={practitionerDesignation}
        />
      )}
    </Box>
  );
};
